import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { isPurchaseUser } from "../utils/permissions";
import { authFetch } from "../utils/authFetch";

function POListPage() {
  const [pos, setPos] = useState([]);
  const [sites, setSites] = useState([]);
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [siteId, setSiteId] = useState("");
  const [status, setStatus] = useState("");
  const [sortBy, setSortBy] = useState("id");
  const [sortDir, setSortDir] = useState("desc");
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, totalPages: 0 });
  const [loading, setLoading] = useState(false);

  const pageSize = 25;

  const loadPOs = async () => {
    setLoading(true);
    try {
      const query = new URLSearchParams({
        page: String(page),
        limit: String(pageSize),
        search,
        site_id: siteId,
        status,
        sort_by: sortBy,
        sort_dir: sortDir
      });
      const res = await authFetch(`${process.env.REACT_APP_API_URL}/po-list?${query.toString()}`);
      const result = await res.json();
      if (!res.ok) throw new Error(result.error || "Unable to load purchase orders");
      setPos(result.data || []);
      setPagination(result.pagination || { page, limit: pageSize, total: 0, totalPages: 0 });
    } catch (err) {
      console.error(err);
      setPos([]);
    } finally {
      setLoading(false);
    }
  };

  const loadSites = async () => {
    try {
      const res = await authFetch(`${process.env.REACT_APP_API_URL}/po-sites`);
      const data = await res.json();
      if (res.ok) setSites(data || []);
    } catch (err) {
      console.error("Unable to load PO sites:", err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this PO?")) return;

    try {
      const res = await authFetch(`${process.env.REACT_APP_API_URL}/delete-po/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Unable to delete PO");
      loadPOs();
    } catch (err) {
      console.error(err);
      alert(err.message);
    }
  };

  useEffect(() => {
    loadSites();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    loadPOs();
  }, [page, search, siteId, status, sortBy, sortDir]);

  const updateFilter = (setter) => (event) => {
    setter(event.target.value);
    setPage(1);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case "Ordered":
        return { background: "#d1fae5", color: "#065f46" };
      case "Pending":
        return { background: "#fef3c7", color: "#92400e" };
      case "Pending Approval":
        return { background: "#fef3c7", color: "#92400e" };
      case "Cancelled":
        return { background: "#fee2e2", color: "#991b1b" };
      default:
        return { background: "#e5e7eb", color: "#374151" };
    }
  };

  return (
    <div style={styles.container}>
      <h2 style={styles.title}>Purchase Orders</h2>
      <div style={styles.header}>
        <select style={styles.siteFilter} value={siteId} onChange={updateFilter(setSiteId)}>
          <option value="">All Sites</option>
          {sites.map((site) => <option key={site.id} value={site.id}>{site.site_name}</option>)}
        </select>

        <input
          type="search"
          placeholder="Search site, vendor, PO ID, status or date..."
          style={styles.search}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />

        <select style={styles.filter} value={status} onChange={updateFilter(setStatus)}>
          <option value="">All Status</option>
          <option value="Ordered">Ordered</option>
          <option value="Draft">Draft</option>
          <option value="Pending Approval">Pending Approval</option>
          <option value="Approved">Approved</option>
          <option value="Cancelled">Cancelled</option>
        </select>

        <select style={styles.filter} value={`${sortBy}:${sortDir}`} onChange={(e) => {
          const [nextSort, nextDirection] = e.target.value.split(":");
          setSortBy(nextSort);
          setSortDir(nextDirection);
          setPage(1);
        }}>
          <option value="id:desc">Newest first</option>
          <option value="date:desc">Latest date</option>
          <option value="date:asc">Oldest date</option>
          <option value="vendor:asc">Vendor A-Z</option>
          <option value="site:asc">Site A-Z</option>
          <option value="status:asc">Status</option>
        </select>

        <button style={styles.addBtn} onClick={() => navigate("/purchase-order")}>+ Create PO</button>
      </div>
      <div style={styles.card}>
        <table style={styles.table}>
          <thead>
            <tr style={styles.theadRow}>
              <th>ID</th>
              <th>Order ID</th>
              <th>Date</th>
              <th>Vendor</th>
              <th>Site</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>

          <tbody>
  {loading ? <tr><td colSpan="7" style={styles.empty}>Loading purchase orders...</td></tr> : pos.map((po) => (
              <tr key={po.id} style={styles.row}>
                <td>{po.id}</td>
                <td style={{ fontWeight: "600" }}>{po.order_id}</td>
                <td>{formatDate(po.date)}</td>
                <td>{po.vendor_name}</td>
                <td>{po.site_name}</td>

                <td>
                  <span
                    style={{
                      ...styles.status,
                      ...getStatusStyle(po.status),
                    }}
                  >
                    {po.status}
                  </span>
                </td>

               <td>{po.status === "Draft" ? (
                  <button
                    style={{
                    ...styles.button,
                    background: "#f59e0b"
                    }}
                   onClick={() =>
                   navigate("/purchase-order", {
                   state: {
                   draftId: po.id
                   }
                  })
                 }
                >
                 Continue
                </button>
                 ) : (
                <button
                style={styles.button}
                onClick={() => navigate(`/print/${po.id}`)}
                >
                  View
                </button>
              )}

            {!isPurchaseUser() && (
           <button
           style={styles.deleteBtn}
           onClick={() => handleDelete(po.id)}
           >
           Delete
          </button>
          )}
         </td>
              </tr>
            ))}
          </tbody>
        </table>

        {!loading && pos.length === 0 && (
          <p style={styles.empty}>No Purchase Orders Found</p>
        )}
        <div style={styles.pagination}>
          <span>{pagination.total} purchase order{pagination.total === 1 ? "" : "s"}</span>
          <div style={styles.paginationControls}>
            <button style={styles.pageButton} disabled={page <= 1 || loading} onClick={() => setPage((current) => current - 1)}>Previous</button>
            <span>Page {pagination.totalPages ? page : 0} of {pagination.totalPages || 0}</span>
            <button style={styles.pageButton} disabled={page >= pagination.totalPages || loading} onClick={() => setPage((current) => current + 1)}>Next</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default POListPage;

const styles = {
  container: {
    padding: "20px",
    background: "#f3f4f6",
    minHeight: "100vh",
  },

  title: {
    marginBottom: "20px",
    fontSize: "24px",
    fontWeight: "700",
  },

  card: {
    background: "#fff",
    borderRadius: "10px",
    padding: "15px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  },

  table: {
    width: "100%",
    borderCollapse: "collapse",
  },

  theadRow: {
    background: "#f9fafb",
    textAlign: "left",
  },

  row: {
    borderBottom: "1px solid #e5e7eb",
    transition: "0.2s",
  },

  status: {
    padding: "5px 10px",
    borderRadius: "20px",
    fontSize: "12px",
    fontWeight: "600",
  },

  button: {
    background: "#2563eb",
    color: "#fff",
    border: "none",
    padding: "6px 12px",
    borderRadius: "6px",
    cursor: "pointer",
  },

  empty: {
    textAlign: "center",
    padding: "20px",
    color: "#6b7280",
  },

  header: {
  display: "flex",
  gap: "10px",
  marginBottom: "15px",
},

search: {
  flex: 1,
  padding: "8px",
  borderRadius: "6px",
  border: "1px solid #ccc",
},

filter: {
  padding: "8px",
  borderRadius: "6px",
  border: "1px solid #ccc",
},

siteFilter: {
  minWidth: "180px",
  padding: "8px",
  borderRadius: "6px",
  border: "1px solid #ccc",
  background: "#fff",
},

addBtn: {
  background: "#16a34a",
  color: "#fff",
  border: "none",
  padding: "8px 14px",
  borderRadius: "6px",
  cursor: "pointer",
},
deleteBtn: {
  background: "#dc2626",
  color: "#fff",
  border: "none",
  padding: "6px 12px",
  borderRadius: "6px",
  cursor: "pointer",
  marginLeft: "5px",
},

pagination: {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "12px",
  marginTop: "16px",
  color: "#64748b",
  fontSize: "13px",
},

paginationControls: {
  display: "flex",
  alignItems: "center",
  gap: "10px",
},

pageButton: {
  padding: "6px 10px",
  border: "1px solid #cbd5e1",
  borderRadius: "6px",
  background: "#fff",
  color: "#334155",
  cursor: "pointer",
}
};
